import { mapValues } from "lodash";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AnySchema, object, ObjectSchema } from "yup";
import { useBasicFieldsValidation } from "../../../../store/launch-configurations/models/basic-fields";
import {
  DefaultFieldNames,
  useDefaultFieldMap,
} from "../../../../store/launch-configurations/models/default-fields";
import { LaunchConfigurationsDTO } from "../../../../store/launch-configurations/models/dto";
import { Field } from "../../../../store/launch-configurations/models/internal/field";
import { FieldHook } from "../../../../store/launch-configurations/models/default-fields/common";

/**
 * This method will traverse the JS object with validation schemas at the leaves
 * and groups them using the shape() method. It will return one large validation schema
 * shaped in the JS object passed.
 * @param a an JS object which has AnySchema as the leaves
 * @returns the same JS object but as a schema
 */

export function useHaveAsObject() {
  const basicFieldsValidation = useBasicFieldsValidation();
  const defaultFields = useDefaultFieldMap();
  const { t } = useTranslation();

  const haveAsObject: (
    a: any,
    config: LaunchConfigurationsDTO.Responses.GetSingle.T
  ) => ObjectSchema<any, any, any, any> = useCallback(
    (a, config) => {
      if (Field.isField(a)) {
        if (Field.isDefaultField(a)) {
          const schemaFn = defaultFields[a.field].schema;
          const schema =
            typeof schemaFn === "function" ? schemaFn(a, config) : schemaFn;

          return defaultFields[a.field].skipOptionalCheck // for some fields we want to skip optional check
            ? schema
            : a.optional
            ? schema.notRequired()
            : schema.required(
                t("required-field", {
                  ns: "errors",
                  field: t(a.field, { ns: "fields" }),
                })
              );
        } else {
          const { schema, skipOptionalCheck } = basicFieldsValidation(a);
          return skipOptionalCheck
            ? schema
            : a.optional
            ? schema.notRequired()
            : schema.required(
                t("required-field", {
                  ns: "errors",
                  field: t(a.field, { ns: "fields" }),
                })
              );
        }
      } else if (typeof a === "object") {
        return object().shape(mapValues(a, (f) => haveAsObject(f, config)));
      } else {
        throw new Error(
          `Error in getting field validation. ${JSON.stringify(a)}`
        );
      }
    },
    [basicFieldsValidation, defaultFields]
  );

  return haveAsObject;
}
